import { Link } from 'react-router-dom'
import { CalendarClock } from 'lucide-react'

import { cn } from '@/lib/cn'
import type { WeekendActivityEntry } from './use-calendar-week'

function formatHours(ms: number): string {
  if (ms <= 0) return ''
  const totalMin = Math.floor(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, '0')}min`
}

function pluralOsoby(n: number): string {
  if (n === 1) return 'osoba'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return 'osoby'
  return 'osób'
}

/**
 * Klinika pracuje pn–pt, więc kalendarz nie ma kolumn sobotnich i niedzielnych.
 * Sesje weekendowe mimo to powstają — przy korekcie admina, po auto-close
 * przechodzącym przez północ albo gdy ktoś faktycznie przyszedł. Ukrycie kolumn
 * nie może oznaczać ukrycia godzin, więc weekend pokazuje się jako wyjątek:
 * niewidoczny, dopóki nic się nie wydarzyło, i wyraźny, gdy się wydarzyło.
 *
 * Tło niesie znaczenie, tekst zostaje w `text-text-primary` — para
 * `text-warning` na `bg-warning/*` nie przechodzi WCAG AA w jasnym motywie.
 */
export default function WeekendActivityNotice({
  entries,
}: {
  entries: WeekendActivityEntry[]
}) {
  if (entries.length === 0) return null

  return (
    <section
      className={cn(
        // Karta jak każda inna (bg-elevated), sygnał niesie SOLIDNY pasek.
        // Tint `warning/10` na tle strony dawał zmierzone 1,10:1, a nawet
        // pełne `border-warning` tylko 2,84:1 — poniżej progu 3:1 dla elementu
        // nietekstowego. Alarm, którego nie widać, nie jest alarmem.
        // Pasek na białej karcie mierzy 3,18:1 (jasny) i 5,19:1 (ciemny).
        'rounded-card border border-border-subtle bg-bg-elevated',
        'border-l-4 border-l-warning',
        'px-3 py-2.5 mt-3',
      )}
    >
      <h2 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-text-primary">
        <CalendarClock className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
        Praca w weekend
      </h2>
      <ul className="mt-1.5 space-y-1">
        {entries.map((entry) => (
          <li key={entry.date}>
            <Link
              to={`/admin/day/${entry.date}`}
              className={cn(
                'inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5',
                'text-xs text-text-primary hover:text-brand-accent',
                'transition-colors duration-150 ease-brand rounded-md min-h-[32px] py-1',
              )}
            >
              <span className="font-medium">
                {entry.weekday} {entry.date.slice(8, 10)}.{entry.date.slice(5, 7)}
              </span>
              <span className="text-text-muted tabular-nums">
                {entry.userCount} {pluralOsoby(entry.userCount)}
                {formatHours(entry.totalDurationMs)
                  ? ` · ${formatHours(entry.totalDurationMs)}`
                  : ''}
                {/* Otwarta sesja liczy się jako 0 h, więc sama suma godzin
                    zaniżałaby dzień i nie mówiła, że ktoś jest nadal zalogowany. */}
                {entry.hasOpenSession ? ' · sesja w toku' : ''}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
